import React, { useCallback, useContext, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose } from "@fortawesome/pro-regular-svg-icons";
import { Blog } from "../../../client/api";
import { ClavaContext } from "../../../config/contexts";
import { showTranslated, translate } from "../../../config/translator";
import ClavaImage from "../ClavaImage";
import RoundedRectangle from "../RoundedRectangle";

const modalRoot = document.getElementById("app") as HTMLElement;

type NewsDetailModalProps = {
  news: Blog;
  onClose: () => void;
};

const NewsDetailModal: React.FC<NewsDetailModalProps> = ({ news, onClose }) => {
  const { l } = useContext(ClavaContext);
  const scrollYRef = useRef(0);

  useEffect(() => {
    const { overflow: originalOverflow, position: originalPosition } = document.body.style;
    scrollYRef.current = window.scrollY;

    document.body.style.overflow = "hidden";
    document.body.style.position = "fixed";
    document.body.style.width = "100%";
    document.body.style.top = `-${scrollYRef.current}px`;

    return () => {
      document.body.style.overflow = originalOverflow;
      document.body.style.position = originalPosition;
      document.body.style.width = "";
      document.body.style.top = "";
      window.scrollTo(0, scrollYRef.current);
    };
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " " || e.key === "Escape") {
        onClose();
      }
    },
    [onClose]
  );

  const handleOpenLink = useCallback(() => {
    if (news.link) {
      window.open(news.link, "_blank", "noopener,noreferrer");
    }
  }, [news.link]);

  const title = showTranslated(news.title, l);
  const body = showTranslated(news.body, l);
  const hasLink = !!news.link?.trim();

  return ReactDOM.createPortal(
    <>
      <div
        role="button"
        tabIndex={0}
        aria-label="Close modal"
        className="fixed inset-0 z-[1000] backdrop-blur backdrop-brightness-90"
        onClick={onClose}
        onKeyDown={handleKeyDown}
      />

      <div className="fixed inset-0 z-[1001] flex items-start justify-center pt-28 px-4 pointer-events-none">
        <div className="w-full max-w-lg pointer-events-auto">
          <RoundedRectangle
            className="w-full bg-bg-secondary dark:bg-bg-secondary-dark shadow-card dark:shadow-card-dark overflow-hidden"
            hasBorder={false}
            hasTitle={false}
          >
            <div className="flex flex-col max-h-[calc(100vh-12rem)] relative">
              <button
                type="button"
                className="absolute top-4 right-4 z-10 w-10 h-10 flex items-center justify-center border-none bg-bg-secondary dark:bg-bg-secondary-dark outline-0 flex-shrink-0 hover:opacity-70 transition-opacity rounded-full"
                onClick={onClose}
                aria-label="Close"
              >
                <FontAwesomeIcon
                  icon={faClose}
                  size="lg"
                  className="text-font dark:text-font-dark"
                />
              </button>

              {news.image && (
                <div className="w-full overflow-hidden rounded-t-xl flex-shrink-0">
                  <ClavaImage
                    image={news.image}
                    width="100%"
                    format="full"
                    className="rounded-t-xl"
                    alt={title}
                  />
                </div>
              )}

              <div className="px-6 py-6 overflow-y-auto flex-1 min-h-0">
                <div className="flex flex-col gap-6">
                  <h2 className="text-xl font-semibold text-font dark:text-font-dark">
                    {title}
                  </h2>

                  <div className="text-font dark:text-font-dark text-sm whitespace-pre-wrap">
                    {body}
                  </div>

                  {hasLink && (
                    <div className="w-full flex justify-center pt-2">
                      <button
                        type="button"
                        onClick={handleOpenLink}
                        className="w-full rounded-full py-2 px-4 flex flex-row items-center justify-center border-0 bg-primary active:bg-btn-active hover:bg-btn-active text-font-dark"
                      >
                        {translate("learnMore", l)}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </RoundedRectangle>
        </div>
      </div>
    </>,
    modalRoot
  );
};

export default NewsDetailModal;

